import { Form, Input, Modal, Select, Tag } from "antd";
import { idToObjectMap, handleDate } from "common/utils";
import { TableFormList } from "components/formList";
import {
  useAddFeePlanRuleMutation,
  useAddFeePlanRuleSetMutation,
  useDeleteFeePlanRuleMutation,
  useFetchFeePlanRulesQuery,
  useFetchFeePlansQuery,
  useUpdateFeePlanRuleMutation,
  useUpdateFeePlanRuleSetMutation,
} from "features/feePlans/feePlansAPI";
import { useFetchProductOfferingsQuery } from "features/productOfferings/productOfferingsAPI";
import { useFetchWorkflowStatesQuery } from "features/workflows/workflowStatesAPI";
import { useMemo } from "react";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

const filterOption = (inputValue, option) => {
  const fullOptionText = option.label;
  return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
};

export default function AddFeePlanRulesetModal({
  title,
  open,
  onOk,
  onCancel,
  selectedRuleSet,
  setSelectedRuleSet,
}) {
  const [form] = Form.useForm();
  const { data: feePlans } = useFetchFeePlansQuery();
  const { data: productOfferings } = useFetchProductOfferingsQuery();
  const { data: defaultWorkflowStates } = useFetchWorkflowStatesQuery();
  const [addFeePlanRule] = useAddFeePlanRuleMutation();
  const [updateFeePlanRule] = useUpdateFeePlanRuleMutation();
  const [addFeePlanRuleSet] = useAddFeePlanRuleSetMutation();
  const [updateFeePlanRuleSet] = useUpdateFeePlanRuleSetMutation();
  const [deleteFeePlanRule] = useDeleteFeePlanRuleMutation();

  const initialValues = {
    ...selectedRuleSet,
    defaultFeePlanId: selectedRuleSet?.defaultFeePlan?.id,
  };

  const isNew = !selectedRuleSet;

  const { data: feePlanRules } = useFetchFeePlanRulesQuery(
    {
      ruleSetId: selectedRuleSet?.id,
    },
    { skip: isNew },
  );

  const productOfferingsById = useMemo(() => {
    return idToObjectMap(productOfferings);
  }, [productOfferings]);

  const save = async () => {
    const fields = await form.validateFields();
    const action = isNew
      ? addFeePlanRuleSet(fields)
      : updateFeePlanRuleSet({ ...fields, id: selectedRuleSet.id });
    const result = await action;

    if (isNew && "data" in result) {
      setSelectedRuleSet(result.data);
    }
    return result;
  };

  const onSubmit = async () => {
    const result = await save();
    if ("data" in result) {
      onOk();
    }
  };

  // We run this hook when Add Fee Plan Rule is pressed
  const preAddFeePlanRowHook = async () => {
    if (isNew) {
      const result = await save();
      if ("error" in result) {
        return false;
      }
    }
  };

  const columns = [
    {
      dataIndex: "feePlanId",
      key: "feePlanId",
      render: (index, rule) =>
        feePlans?.find((feePlan) => feePlan.id === rule.feePlanId)?.name ??
        `Fee Plan #${rule.feePlanId}`,
      editable: true,
      inputType: "select",
      title: "Fee Plan",
      inputProps: {
        showSearch: true,
        filterOption,
      },
      options: feePlans?.map((feePlan) => ({ label: feePlan.name, value: feePlan.id })),
    },
    {
      title: "Account Status",
      dataIndex: "workflowStateIds",
      key: "workflowStateIds",
      editable: true,
      inputType: "select",
      inputProps: {
        mode: "multiple",
        filterOption,
      },
      width: 600,
      options: defaultWorkflowStates?.map((state) => ({
        value: state.id,
        label: [state.code, state.name].join(" - "),
      })),
      render: (index, rule) => {
        const workflowStates =
          defaultWorkflowStates?.filter((state) => rule.workflowStateIds.includes(state.id)) ?? [];

        return (
          workflowStates?.map((state) => (
            <Tag>
              {state.code} - {state.name}
            </Tag>
          )) ?? "-"
        );
      },
    },
    {
      title: "Legal Status",
      dataIndex: "legalStatuses",
      key: "legalStatuses",
      editable: true,
      inputType: "select",
      inputProps: {
        mode: "multiple",
        showSearch: true,
        filterOption,
      },
      options: [
        { label: "Lawsuit Only", value: "in_litigation", key: "in_litigation" },
        { label: "Judgment", value: "judgment", key: "judgment" },
        {
          label: "Legal Payment Methods",
          value: "has_legal_payment_method",
          key: "has_legal_payment_method",
        },
        {
          label: "Assigned Legal Forwarding Entity",
          value: "has_legal_forwarding_entity",
          key: "has_legal_forwarding_entity",
        },
      ],
      render: (index, rule) =>
        rule.legalStatuses?.map(
          (status) =>
            ({
              in_litigation: (
                <Tag color="orange" key={`rule-${rule?.id}-in-litigation`}>
                  In Litigation
                </Tag>
              ),
              judgment: (
                <Tag color="yellow" key={`rule-${rule?.id}-judgment`}>
                  Judgment
                </Tag>
              ),
              has_legal_payment_method: (
                <Tag color="blue" key={`rule-${rule?.id}-has-legal-payment-method`}>
                  Legal Payment Methods
                </Tag>
              ),
              has_legal_forwarding_entity: (
                <Tag color="purple" key={`rule-${rule?.id}-has-legal-forwarding-entity`}>
                  Legal Forwarding Entity
                </Tag>
              ),
            }[status]),
        ) ?? "-",
    },
    {
      title: "Product",
      dataIndex: "productOfferingId",
      key: "productOfferingId",
      editable: true,
      inputType: "select",
      inputProps: {
        showSearch: true,
        filterOption,
      },
      options: productOfferings?.map((productOffering) => ({
        label: productOffering.name,
        value: productOffering.id,
      })),
      render: (index, rule) => productOfferingsById[rule.productOfferingId]?.name,
    },
    {
      dataIndex: "minAccountBalance",
      key: "minAccountBalance",
      editable: true,
      inputType: "number",
      inputProps: {
        step: 0.01,
      },
      title: "Account Original Balance (From)",
    },
    {
      dataIndex: "maxAccountBalance",
      key: "maxAccountBalance",
      editable: true,
      inputType: "number",
      inputProps: {
        step: 0.01,
      },
      title: "Account Original Balance (To)",
    },
    {
      dataIndex: "minAccountServiceAgeDays",
      key: "minAccountServiceAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days Since Service Date (From)",
    },
    {
      dataIndex: "maxAccountServiceAgeDays",
      key: "maxAccountServiceAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Service Date (To)",
    },
    {
      dataIndex: "minAccountAgeDays",
      key: "minAccountAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days in Aktos (From)",
    },
    {
      dataIndex: "maxAccountAgeDays",
      key: "maxAccountAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days in Aktos (To)",
    },
    {
      dataIndex: "minAccountUploadAgeDays",
      key: "minAccountUploadAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days Since Upload (From)",
    },
    {
      dataIndex: "maxAccountUploadAgeDays",
      key: "maxAccountUploadAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Upload (To)",
    },
    {
      dataIndex: "minFirstDelinquencyAgeDays",
      key: "minFirstDelinquencyAgeDays",
      editable: true,
      required: false,
      inputType: "number",
      title: "Days Since First Delinquency (From)",
    },
    {
      dataIndex: "maxFirstDelinquencyAgeDays",
      key: "maxFirstDelinquencyAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since First Delinquency (To)",
    },
    {
      dataIndex: "minDateOfFirstDelinquency",
      key: "minDateOfFirstDelinquency",
      editable: true,
      required: false,
      inputType: "date",
      title: "Date of First Delinquency (From)",
    },
    {
      dataIndex: "maxDateOfFirstDelinquency",
      key: "maxDateOfFirstDelinquency",
      required: false,
      editable: true,
      inputType: "date",
      title: "Date of First Delinquency (To)",
    },
    {
      dataIndex: "minTurnoverDate",
      key: "minTurnoverDate",
      editable: true,
      required: false,
      inputType: "date",
      title: "Turnover Date (From)",
    },
    {
      dataIndex: "maxTurnoverDate",
      key: "maxTurnoverDate",
      required: false,
      editable: true,
      inputType: "date",
      title: "Turnover Date (To)",
    },
    {
      dataIndex: "minTurnoverAgeDays",
      key: "minTurnoverAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Turnover (From)",
    },
    {
      dataIndex: "maxTurnoverAgeDays",
      key: "maxTurnoverAgeDays",
      required: false,
      editable: true,
      inputType: "number",
      title: "Days Since Turnover (To)",
    },
  ];

  return (
    <Modal
      width={1200}
      maskClosable={false}
      title={title}
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
    >
      <StyledForm
        form={form}
        layout="vertical"
        validateMessages={{ required: "This is a required field" }}
        initialValues={initialValues}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "This field is required." }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="defaultFeePlanId"
          label="Default Fee Plan"
          tooltip="The fee plan to use if no fee plan rules are matched."
        >
          <Select
            showSearch
            popupMatchSelectWidth={false}
            filterOption={filterOption}
            placeholder="Select Fee Plan"
            options={feePlans?.map((feePlan) => ({ label: feePlan.name, value: feePlan.id }))}
          />
        </Form.Item>
      </StyledForm>
      <h5>Fee Plan Rules</h5>
      Define fee plan rules, which define which fee plan to use for each account criteria matched.
      <br />
      <br />
      <TableFormList
        addText="Add Rule"
        columns={columns}
        data={isNew ? [] : feePlanRules}
        onDelete={(feePlanRule) =>
          deleteFeePlanRule({
            ruleSetId: selectedRuleSet.id,
            ...feePlanRule,
          })
        }
        onSave={async (feePlanRule) => {
          const action = feePlanRule?.id ? updateFeePlanRule : addFeePlanRule;
          const {
            minTurnoverDate,
            maxTurnoverDate,
            minDateOfFirstDelinquency,
            maxDateOfFirstDelinquency,
            ...feePlanRuleFields
          } = feePlanRule;
          const payload = {
            ruleSetId: selectedRuleSet.id,
            ...feePlanRuleFields,
          };

          // Helper function to safely format dates
          payload.minTurnoverDate = handleDate(minTurnoverDate, true);
          payload.maxTurnoverDate = handleDate(maxTurnoverDate, true);
          payload.minDateOfFirstDelinquency = handleDate(minDateOfFirstDelinquency, true);
          payload.maxDateOfFirstDelinquency = handleDate(maxDateOfFirstDelinquency, true);

          return action(payload);
        }}
        preAddRowHook={preAddFeePlanRowHook}
      />
    </Modal>
  );
}
